import {
  Component, ElementRef, Input, ViewChild,
  SimpleChanges, OnChanges, OnDestroy
} from '@angular/core';
import { IEmbedConfiguration,  Report as PbiReport } from 'powerbi-client';
import { map, tap } from 'rxjs/operators';

import { SelectedReportName } from '@app/services/report-name.service';
import { PowerBiConfigBuilder } from './services/power-bi-config.builder';
import { Report } from '@app/shared/features/reports/models/report.model';
import { PowerBiFilterData, PowerBiSlicerData } from './models/power-bi-data.model';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IBasicFilter,
  FilterType,
  FiltersOperations
} from 'powerbi-models';
import * as pbi from 'powerbi-client';
import { Subscription } from 'rxjs';
import { ReportFilterSchemas } from './enums/report-filters.enum';

@Component({
  selector: 'app-power-bi',
  templateUrl: './power-bi.component.html',
  styleUrls: ['./power-bi.component.scss']
})
export class PowerBiComponent implements OnChanges, OnDestroy {

  @Input() public report: Report;
  @Input() public pageNavigationVisible = false;
  @Input() public filtersData: PowerBiFilterData[];
  @Input() public slicersData: PowerBiSlicerData[];
  @Input() public selectedQuarter = '';
  @Input() public quarterTableName: string = null;

  @ViewChild('pbContainer', { static: true })
  private pbContainer: ElementRef;

  powerbiInstance!: pbi.service.Service;
  
  private embeddedReport: PbiReport | null = null;
  private cachedPcValue: string | null = null;
  private isReloading = false;
  private readonly subscription = new Subscription();

  private lastAppliedQuarter: string | null = null;

  constructor(
    private readonly powerBiConfigBuilder: PowerBiConfigBuilder,
    private readonly selectedReportName: SelectedReportName,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) { 
    this.powerbiInstance = new pbi.service.Service(
      pbi.factories.hpmFactory,
      pbi.factories.wpmpFactory,
      pbi.factories.routerFactory
    );
  }


  ngOnChanges(changes: SimpleChanges): void {
    const pbiContainer = this.pbContainer.nativeElement;
    const { report, isPreview, selectedQuarter, filtersData } = changes;

    // Cache PC
    if (filtersData?.currentValue) {
      const pcFilter = filtersData.currentValue.find(f =>
        f.table === 'Location Data' && f.column === 'code'
      );
      if (pcFilter?.value) {
        this.cachedPcValue = pcFilter.value as string;
      }
    }

    if (report) {
      this.selectedReportName.reportName = report.currentValue.name;
    }

    // FULL reload
    if (report?.currentValue?.name || (isPreview && !isPreview.firstChange)) {
      this.resetAndLoad(pbiContainer);
      return;
    }

    // Quarter change
    if (
      selectedQuarter &&
      !selectedQuarter.firstChange &&
      selectedQuarter.currentValue !== selectedQuarter.previousValue &&
      !this.isReloading &&
      this.embeddedReport
    ) {
      this.handleQuarterChange();
    }
  }

  private resetAndLoad(container: any): void {
    this.isReloading = false;
    this.lastAppliedQuarter = null;

    this.powerbiInstance.reset(container);
    this.embeddedReport = null;

    this.load(this.report, [...this.filtersData]);
  }

  private handleQuarterChange(): void {
    if (!this.embeddedReport) return;

    if (this.selectedQuarter === this.lastAppliedQuarter) return;

    this.lastAppliedQuarter = this.selectedQuarter;

    // 🚀 DIRECT APPLY (NO VISUAL DETECTION NEEDED)
    this.setPcActivityPeriodSlicer();
  }

  // ✅ FIXED SLICER
  private async setPcActivityPeriodSlicer(): Promise<void> {
    try {
      if (!this.embeddedReport) return;

      const filters: IBasicFilter[] = [];

      if (this.cachedPcValue) {
        filters.push({
          $schema: ReportFilterSchemas.Basic,
          target: { table: "Location Data", column: "code" },
          operator: "In",
          values: [this.cachedPcValue],
          filterType: FilterType.Basic
        });
      }

      if (this.selectedQuarter) {
        filters.push({
          $schema: ReportFilterSchemas.Basic,
          target: { table: this.quarterTableName, column: "period" },
          operator: "In",
          values: [this.selectedQuarter],
          filterType: FilterType.Basic
        });
      }


      // 🚀 REPLACE ALL FILTERS (GLOBAL FIX)
      await this.embeddedReport.updateFilters(
        FiltersOperations.Replace,
        filters as any
      );

    } catch (err) {
      this.reloadWithQuarter();
    }
  }

  private reloadWithQuarter(): void {
    if (this.isReloading) return;

    this.isReloading = true;

    const container = this.pbContainer.nativeElement;
    this.powerbiInstance.reset(container);
    this.embeddedReport = null;

    this.load(this.report, [...this.filtersData]);
  }

  // ✅ FIXED INITIAL LOAD FILTER
  private load(
    report: Report,
    filtersSnapshot: PowerBiFilterData[]
  ): void {

    this.lastAppliedQuarter = this.selectedQuarter || null;

    const sub = this.powerBiConfigBuilder
      .setReportBaseInfo(report)
      .pipe(
        tap(() => {
          const updatedFilters = [...filtersSnapshot];

          // ✅ ONLY use PowerBiFilterData here
          if (this.selectedQuarter) {
            updatedFilters.push({
              table: this.quarterTableName,
              column: 'period',
              value: this.selectedQuarter
            });
          }

          this.powerBiConfigBuilder.setFilters(updatedFilters);
        }),
        tap(() => this.powerBiConfigBuilder.setBasicSlicers(this.slicersData)),
        tap(() => this.powerBiConfigBuilder.setAdvancedSlicers(this.slicersData)),
        tap(() => this.powerBiConfigBuilder.setPageNavigation(this.pageNavigationVisible)),
        map(() => this.powerBiConfigBuilder.getConfig())
      )
      .subscribe(config => this.render(config));

    this.subscription.add(sub);
  }
  private render(config: IEmbedConfiguration): void {
    const container = this.pbContainer.nativeElement;

    this.powerbiInstance.reset(container);

    const report = this.powerbiInstance.embed(container, config) as PbiReport;
    this.embeddedReport = report;

    report.off('rendered');

    report.on('rendered', () => {
      this.isReloading = false;
    });

    report.on('selectionChanged', () => this.addQueryParams(report));
    report.on('visualClicked', () => this.addQueryParams(report));
  }

  private addQueryParams(report: PbiReport): void {
    report.getActivePage()
      .then(p => p.getVisuals())
      .then(v => v.filter(x => x.type === 'slicer'))
      .then(async s => Promise.all(s.map(x => x.getSlicerState())))
      .then(() => this.updateUrl());
  }

  private updateUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge'
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.embeddedReport = null;
  }
}